import { useMemo } from 'react';
import { Button, Collapse, HStack, Link, Stack } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TemplateTitleField } from '../TemplateTitleField/TemplateTitleField';
import { DescriptionField } from '../DescriptionField/DescriptionField';
import { FastCategoryTree } from '../FastCategoryTree';
import { AdditionalQuestionsField } from '../AdditionalQuestionsField';
import { useHtmlFormInputs } from '../../hooks/useHtmlFormInputs/useHtmlFormInputs';
import { useProvideDetails } from '../../hooks/useProvideDetails';

export type ProvideDetailsProps = {
    initial: string;
    editmode: string;
    inputs: ReturnType<typeof useHtmlFormInputs>;
};

export function ProvideDetails({ initial, editmode, inputs }: ProvideDetailsProps) {
    const [t, { language }] = useTranslation();
    const { isOpen, templateOptions, billingQuestion } = useProvideDetails({ inputs });
    const {
        formState: { isSubmitting },
    } = useFormContext();

    const cancelHref = useMemo(() => {
        const queryParams = new URLSearchParams(window.location.search);
        return `/${language}/fast/back/?${queryParams.toString()}`;
    }, []);

    return (
        <>
            <FastCategoryTree initial={initial} input={inputs.subcategory} />
            <AdditionalQuestionsField input={inputs.billing_answer} question={billingQuestion} />

            <Collapse in={isOpen} animateOpacity unmountOnExit={false}>
                <Stack spacing={12} mt={12}>
                    <TemplateTitleField
                        options={templateOptions}
                        nameInput={inputs.name}
                        dealNameTemplateInput={inputs.deal_name_template}
                    />
                    <DescriptionField input={inputs.description} />
                </Stack>
            </Collapse>

            {editmode === 'true' ? (
                <HStack gap="3rem" mt={4}>
                    <Button
                        type="submit"
                        colorScheme="yellow"
                        minWidth="15rem"
                        isDisabled={isSubmitting}
                    >
                        {t('fast.provide_details.save')}
                    </Button>
                    <BackLink href={cancelHref}>
                        <span>{t('fast.provide_details.cancel')}</span>
                    </BackLink>
                </HStack>
            ) : (
                <Button
                    type="submit"
                    colorScheme="yellow"
                    minWidth="15rem"
                    mt={4}
                    isDisabled={isSubmitting}
                >
                    {t('fast.provide_details.next')}
                </Button>
            )}
        </>
    );
}

const BackLink = styled(Link)`
    font-size: var(--ck-fontSizes-md);
    font-style: normal;
    font-weight: bold
    line-height: 1;
    color: var(--ck-colors-general-dark);
    text-decoration: none;
    padding: 1.12rem 2rem;

    &:hover,
    &:focus {
        color: var(--ck-colors-general-dark);
        text-decoration: none;
    }

    & span {
        position: relative;

        &::after {
            content: '';
            position: absolute;
            bottom: -2px;
            left: 0;
            right: 0;
            width: 100%;
            height: 1.5px;
            border-radius: 2px;
            background: var(--ck-colors-general-dark);
            transform: scaleY(1) scaleX(0);
            transform-origin: center;
            transition: transform 0.2s ease-in-out;
        }
    }

    &:hover span::after,
    &:focus span::after {
        transform: scaleY(1) scaleX(1);
    }
`;
